import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { schedule } from "@ember/runloop";
import { service } from "@ember/service";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import dDraggable from "discourse/ui-kit/modifiers/d-draggable";
import { i18n } from "discourse-i18n";

const LINK_NAME_PREFIX = "game-";

export default class CommunityGameOrderHandle extends Component {
  @service a11y;
  @service communityGameOrder;

  @tracked dragging = false;

  moved = false;

  get label() {
    return i18n("community_navigation.reorder_game", {
      game: this.args.suffixArgs.gameName,
    });
  }

  @action
  didStartDrag(event) {
    if (event.button !== undefined && event.button !== 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.dragging = true;
    this.moved = false;
  }

  @action
  dragMove(event) {
    if (!this.dragging) {
      return;
    }

    event.preventDefault();
    const pointer = event.touches?.[0] || event;
    const target = document
      .elementFromPoint(pointer.clientX, pointer.clientY)
      ?.closest('[data-list-item-name^="game-"]');
    const targetName = target?.dataset.listItemName;
    if (!targetName?.startsWith(LINK_NAME_PREFIX)) {
      return;
    }

    const targetGameKey = targetName.slice(LINK_NAME_PREFIX.length);
    const bounds = target.getBoundingClientRect();
    const placement =
      pointer.clientY > bounds.top + bounds.height / 2 ? "after" : "before";

    this.moved =
      this.communityGameOrder.move(
        this.args.suffixArgs.gameKey,
        targetGameKey,
        placement
      ) || this.moved;
  }

  @action
  didEndDrag() {
    if (this.moved) {
      this.communityGameOrder.persist();
    }

    this.dragging = false;
    this.moved = false;
  }

  @action
  keydown(event) {
    const offset = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
    if (!offset) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    const position = this.communityGameOrder.moveBy(
      this.args.suffixArgs.gameKey,
      offset
    );
    if (position === undefined) {
      return;
    }

    this.communityGameOrder.persist();
    const linkName = `${LINK_NAME_PREFIX}${this.args.suffixArgs.gameKey}`;
    schedule("afterRender", () => {
      document
        .querySelector(
          `[data-list-item-name="${CSS.escape(linkName)}"] .community-game-order-handle`
        )
        ?.focus();
    });
    this.a11y.announce(
      i18n("community_navigation.game_moved", {
        game: this.args.suffixArgs.gameName,
        position: position + 1,
      }),
      "polite"
    );
  }

  @action
  preventNavigation(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  <template>
    <button
      type="button"
      aria-label={{this.label}}
      title={{this.label}}
      class={{dConcatClass
        "community-game-order-handle"
        (if this.dragging "is-dragging")
      }}
      {{dDraggable
        didStartDrag=this.didStartDrag
        dragMove=this.dragMove
        didEndDrag=this.didEndDrag
      }}
      {{on "keydown" this.keydown}}
      {{on "click" this.preventNavigation}}
    >
      {{dIcon "grip-vertical"}}
    </button>
  </template>
}
